import Stack from "@mui/material/Stack";
import { theme } from "@polifonia/theme";
import { fakeT } from "@polifonia/utils/react/fakeT";
import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { Typography } from "@/components/Typography";
import type { InterludeTypes } from "@/interlude";

type ChannelType = Extract<
  InterludeTypes["Channel"]["type"],
  "whatsapp" | "fb" | "ig"
>;

namespace FailedConnectModal {
  export interface OwnProps {
    /**
     * The type of channel that failed to connect
     */
    channelType: "whatsapp" | "fb" | "ig";
    /**
     * Override the default failed message
     */
    message?: string;
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
  }
  export type Props = OwnProps;
}

const t = fakeT;
const translationMap: Record<
  ChannelType,
  Record<"title" | "desc", ReturnType<typeof t>>
> = {
  fb: {
    title: t("page.channel.fb.failedConnect.modal.title"),
    desc: t("page.channel.fb.failedConnect.modal.description"),
  },
  ig: {
    title: t("page.channel.ig.failedConnect.modal.title"),
    desc: t("page.channel.ig.failedConnect.modal.description"),
  },
  whatsapp: {
    title: t("page.channel.whatsapp.failedConnect.modal.title"),
    desc: t("page.channel.whatsapp.failedConnect.modal.description"),
  },
};

export const FailedConnectModal: FC<FailedConnectModal.Props> = ({
  channelType,
  message,
  open,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      handleClose={onClose}
      title={t(translationMap[channelType].title)}
      footer={
        <Stack direction="row" justifyContent="flex-end" gap="12px">
          <Button variant="primary" onClick={onConfirm}>
            {t("common.confirm")}
          </Button>
        </Stack>
      }
    >
      <Stack gap="16px">
        <Typography variant="body" color={theme.colors.staticFgBody}>
          {message ?? t(translationMap[channelType].desc)}
        </Typography>
      </Stack>
    </Modal>
  );
};
